import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { noHlsUrlValidator, urlValidator } from '@app/core/utils';
import { StreamApi } from '@app/features/app-content/models/app-config.model';
import { FetchMountPointsRequest } from '@app/features/app-content/states/app-configuration.actions';
import {
  AppConfigurationState,
  UserRadio,
} from '@app/features/app-content/states/app-configuration.state';
import { DropdownOption } from '@app/ui/components/dropdown/dropdown.model';
import { SelectedImage } from '@app/ui/components/image-upload/image-upload.component';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Subscription, filter } from 'rxjs';

// stream types only offered to apps entitled to them; inserted before the 'autre' option
const OPTIONAL_STREAM_TYPE_OPTIONS: Record<string, DropdownOption> = {
  indes_radios: { value: 'indes_radios', label: 'Indés Radios', disabled: false },
  hls: { value: 'hls', label: 'HLS', disabled: false },
};

export interface StreamEditModalData {
  stream?: StreamApi;
  index?: number;
  existingStreams?: StreamApi[];
}

export interface StreamEditModalResult {
  stream: Partial<StreamApi>;
  logoFile?: File;
  logoDisplayUrl?: string;
}

@Component({
  selector: 'rk-stream-edit-modal',
  templateUrl: './stream-edit-modal.component.html',
  styleUrls: ['./stream-edit-modal.component.scss'],
  standalone: false,
})
export class StreamEditModalComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<StreamEditModalComponent>);
  readonly data: StreamEditModalData = inject(MAT_DIALOG_DATA);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);

  userRadios$ = this.store.select(AppConfigurationState.userRadios);
  icecastMountPoints$ = this.store.select(AppConfigurationState.icecastMountPoints);
  titrationIndesRadiosAvailable$ = this.store.select(AppConfigurationState.titrationIndesRadiosAvailable);
  hlsAvailable$ = this.store.select(AppConfigurationState.hlsAvailable);

  isEditMode = false;
  duplicateStreamName: string | null = null;
  logoFile: File | null = null;
  logoDisplayUrl: string | null = null;
  logoUrl: string | null = null;
  private initialLogoUrl: string | null = null;
  private initialStreamType: string | null = null;
  private initialUserRadio: string | null = null;
  private initialMountPoint: string | null = null;

  form: FormGroup;
  currentStreamType: DropdownOption;
  currentUserRadio: DropdownOption;
  currentIcecastMountPoint: DropdownOption;

  userRadioDropdownOptions: DropdownOption[] = [];
  icecastMountpoints: DropdownOption[] = [];
  streamTypeOptions: DropdownOption[] = [
    { value: 'radioking', label: 'RadioKing', disabled: false },
    { value: 'shoutcast', label: 'Shoutcast v1', disabled: false },
    { value: 'icecast', label: 'Icecast', disabled: false },
    { value: 'autre', label: this.translate.instant('stream-edit.other'), disabled: false },
  ];

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.isEditMode = !!this.data?.stream;

    this.form = new FormGroup({
      nomFlux: new FormControl(this.data?.stream?.nomFlux?.trim() || '', [Validators.required]),
      urlFlux: new FormControl(this.data?.stream?.urlFlux || '', [
        Validators.required,
        urlValidator,
      ]),
      urlTitrage: new FormControl(this.data?.stream?.urlTitrage || '', [urlValidator]),
    });

    // check for duplicate stream
    this.subscriptions.add(
      this.form.get('urlFlux').valueChanges.subscribe(() => this.checkDuplicate()),
    );

    // set initial stream type
    const userRadios = this.store.selectSnapshot(AppConfigurationState.userRadios);
    const defaultType = userRadios?.length ? 'radioking' : 'autre';
    const initialType = this.data?.stream?.typeFlux || defaultType;
    // optional types are appended asynchronously: resolve them from the catalogue
    // so an existing stream keeps the type returned by the API
    this.currentStreamType =
      this.streamTypeOptions.find(o => o.value === initialType) ||
      OPTIONAL_STREAM_TYPE_OPTIONS[initialType] ||
      this.streamTypeOptions.find(o => o.value === 'autre');

    // set logo if editing
    if (this.data?.stream?.logofluxappfield) {
      const raw = this.data.stream.logofluxappfield;
      if (raw.includes('data:image')) {
        this.logoUrl = raw;
      } else {
        this.logoUrl = `https://www.${environment.urls.RADIOKING_DOMAIN}/upload/applications/${raw}`;
      }
    }

    this.updateStreamUrlValidators();

    this.initialLogoUrl = this.logoUrl;
    this.initialStreamType = this.currentStreamType?.value ?? null;

    // add indes_radios option if available
    this.subscriptions.add(
      this.titrationIndesRadiosAvailable$
        .pipe(filter(isAvailable => isAvailable))
        .subscribe(() => this.addOptionalStreamType('indes_radios')),
    );

    // add hls option if available
    this.subscriptions.add(
      this.hlsAvailable$
        .pipe(filter(isAvailable => isAvailable))
        .subscribe(() => this.addOptionalStreamType('hls')),
    );

    // user radios
    this.subscriptions.add(
      this.userRadios$.pipe(filter(radios => !!radios)).subscribe(radios => {
        this.userRadioDropdownOptions = radios.map((radio: UserRadio) => ({
          value: radio.idradio.toString(),
          label: radio.name,
          disabled: false,
        }));
        if (this.data?.stream?.idradio) {
          this.currentUserRadio = this.userRadioDropdownOptions.find(
            o => o.value === this.data.stream.idradio.toString(),
          ) || this.userRadioDropdownOptions[0];
        } else {
          this.currentUserRadio = this.userRadioDropdownOptions[0];
        }
        if (this.initialUserRadio === null) {
          this.initialUserRadio = this.currentUserRadio?.value ?? null;
        }
        this.checkDuplicate();
      }),
    );

    // icecast mount points
    this.subscriptions.add(
      this.icecastMountPoints$.pipe(filter(mp => !!mp)).subscribe(mountPoints => {
        this.icecastMountpoints = mountPoints.map(mp => ({
          value: mp,
          label: mp,
          disabled: false,
        }));
        if (this.data?.stream?.mountPoint) {
          this.currentIcecastMountPoint = this.icecastMountpoints.find(
            o => o.value === this.data.stream.mountPoint,
          );
        } else {
          this.currentIcecastMountPoint = this.icecastMountpoints[0];
        }
        if (this.initialMountPoint === null) {
          this.initialMountPoint = this.currentIcecastMountPoint?.value ?? null;
        }
      }),
    );

    // fetch mount points if icecast
    if (this.currentStreamType.value === 'icecast' && this.data?.stream?.urlFlux) {
      this.store.dispatch(new FetchMountPointsRequest(this.data.stream.urlFlux));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get showStreamUrl(): boolean {
    return this.currentStreamType?.value !== 'radioking';
  }

  get showTitleUrl(): boolean {
    return ['autre', 'indes_radios', 'hls'].includes(this.currentStreamType?.value);
  }

  /** Whether the current app is allowed to use HLS streams. */
  get isHlsAvailable(): boolean {
    return !!this.store.selectSnapshot(AppConfigurationState.hlsAvailable);
  }

  get showMountPoint(): boolean {
    return this.currentStreamType?.value === 'icecast';
  }

  get showUserRadio(): boolean {
    return this.currentStreamType?.value === 'radioking';
  }

  onStreamTypeChange(value: string): void {
    this.currentStreamType = this.streamTypeOptions.find(o => o.value === value);
    this.updateStreamUrlValidators();
    if (value === 'icecast' && this.form.get('urlFlux').value) {
      this.store.dispatch(new FetchMountPointsRequest(this.form.get('urlFlux').value));
    }
    this.checkDuplicate();
  }

  onUserRadioChange(radioId: string): void {
    this.currentUserRadio = this.userRadioDropdownOptions.find(o => o.value === radioId);
    this.checkDuplicate();
  }

  onMountPointChange(mountPoint: string): void {
    this.currentIcecastMountPoint = this.icecastMountpoints.find(o => o.value === mountPoint);
  }

  onImageSelected(event: SelectedImage): void {
    this.logoFile = event.file;
    this.logoDisplayUrl = event.imageUrl.toString();
    this.logoUrl = event.imageUrl.toString();
  }

  onRemoveImage(): void {
    this.logoFile = null;
    this.logoDisplayUrl = null;
    this.logoUrl = null;
  }

  onCancel(): void {
    const hasChanges = this.form.dirty
      || this.logoFile !== null
      || this.logoUrl !== this.initialLogoUrl
      || (this.currentStreamType?.value ?? null) !== this.initialStreamType
      || (this.currentUserRadio?.value ?? null) !== this.initialUserRadio
      || (this.currentIcecastMountPoint?.value ?? null) !== this.initialMountPoint;
    if (hasChanges && !window.confirm(this.translate.instant('stream-modal.confirm-close'))) {
      return;
    }
    this.dialogRef.close();
  }

  onSubmit(): void {
    // trim name before validation so Validators.required catches whitespace-only values
    const nomFluxControl = this.form.get('nomFlux');
    nomFluxControl.setValue(nomFluxControl.value?.trim());

    this.form.markAllAsTouched();
    if (this.showStreamUrl && this.form.get('urlFlux').invalid) {
      return;
    }
    if (nomFluxControl.invalid) {
      return;
    }

    // HLS streams can only be saved when the app is entitled to them
    const isHls = this.currentStreamType.value === 'hls';
    if (isHls && !this.isHlsAvailable) {
      return;
    }

    const streamData: Partial<StreamApi> = {
      nomFlux: nomFluxControl.value,
      typeFlux: this.currentStreamType.value,
      urlFlux: this.form.get('urlFlux').value,
      urlTitrage: this.form.get('urlTitrage').value || null,
      typeTitrage:
        this.currentStreamType.value === 'autre' || isHls
          ? 'web'
          : this.currentStreamType.value,
      logofluxappfield: this.logoUrl || null,
      mountPoint: this.currentIcecastMountPoint?.value || null,
      idradio: this.currentUserRadio ? Number(this.currentUserRadio.value) : null,
    };

    this.dialogRef.close({
      stream: streamData,
      logoFile: this.logoFile,
      logoDisplayUrl: this.logoDisplayUrl,
    });
  }

  /**
   * Adds an optional stream type to the selector, right before the 'autre' option.
   *
   * @param type The optional stream type key, as returned by the API.
   */
  private addOptionalStreamType(type: string): void {
    if (this.streamTypeOptions.some(o => o.value === type)) {
      return;
    }
    const autreIndex = this.streamTypeOptions.findIndex(o => o.value === 'autre');
    const insertIndex = autreIndex >= 0 ? autreIndex : this.streamTypeOptions.length;
    this.streamTypeOptions.splice(insertIndex, 0, { ...OPTIONAL_STREAM_TYPE_OPTIONS[type] });
    // keep the current selection pointing at the instance held by the option list
    this.currentStreamType =
      this.streamTypeOptions.find(o => o.value === this.currentStreamType?.value) ||
      this.currentStreamType;
  }

  /**
   * Refreshes the audio URL validators: only HLS streams may use a `.m3u8` URL.
   */
  private updateStreamUrlValidators(): void {
    const urlFluxControl = this.form.get('urlFlux');
    const validators = [Validators.required, urlValidator];
    if (this.currentStreamType?.value !== 'hls') {
      validators.push(noHlsUrlValidator);
    }
    urlFluxControl.setValidators(validators);
    urlFluxControl.updateValueAndValidity({ emitEvent: false });
  }

  private checkDuplicate(): void {
    if (!this.data?.existingStreams?.length) {
      this.duplicateStreamName = null;

      return;
    }
    let match: StreamApi | undefined;
    if (this.currentStreamType?.value === 'radioking') {
      const radioId = this.currentUserRadio?.value;
      if (radioId) {
        match = this.data.existingStreams.find(
          (s, i) => s.typeFlux === 'radioking' && s.idradio?.toString() === radioId && i !== this.data.index,
        );
      }
    } else {
      const url = this.form.get('urlFlux').value;
      if (url) {
        match = this.data.existingStreams.find(
          (s, i) => s.urlFlux === url && i !== this.data.index,
        );
      }
    }
    this.duplicateStreamName = match?.nomFlux ?? null;
  }
}
